import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useMemo } from 'react'
import { useGroupOrCrossSellWidget } from '@/hooks/useGroupOrCrossSellWidget'
import { useVenue } from '@/hooks/useVenue'
import { useWidgetSettings } from '@/hooks/useWidgetSettings'
import { useGroupAvailabilityDatesQuery, useListAvailabilityDatesQuery } from '@/Reservations/Search/AvailabilityDateApi'
import { DateOnly, Formats, legacyJSDateAdapter } from '@sevenrooms/timepiece'

export function useAvailabilityDates() {
  const { maxDaysOut } = useWidgetSettings()
  const { urlKey, venueToday } = useVenue()
  const { isGroupWidget, venuesInfo } = useGroupOrCrossSellWidget()
  const currentDateAtVenue = useMemo(() => DateOnly.fromSafe(venueToday)?.toJsDate() || new Date(), [venueToday])

  const { isFetching: isDatesFetching, data: availabilityDates = [] } = useListAvailabilityDatesQuery(
    isGroupWidget
      ? skipToken
      : {
          venue: urlKey,
          numDays: maxDaysOut,
          startDate: legacyJSDateAdapter(new Date(), Formats.MonthDayYearByDash),
        }
  )

  const { data: groupWidgetAvailabilityDates = [], isFetching: isGroupDatesFetching } = useGroupAvailabilityDatesQuery(
    isGroupWidget && venuesInfo
      ? {
          venuesInfo,
          maxDaysOut,
        }
      : skipToken
  )

  const isDayBlocked = useCallback(
    (date: Date) => {
      if (isGroupWidget) {
        if (groupWidgetAvailabilityDates) {
          return !groupWidgetAvailabilityDates.includes(DateOnly.fromDate(date).toIso())
        }
        return date < currentDateAtVenue
      }
      return !availabilityDates.includes(DateOnly.fromDate(date).toIso())
    },
    [availabilityDates, currentDateAtVenue, groupWidgetAvailabilityDates, isGroupWidget]
  )

  return isGroupWidget
    ? { isFetching: isDatesFetching, availabilityDates, isDayBlocked }
    : { isFetching: isGroupDatesFetching, availabilityDates: groupWidgetAvailabilityDates, isDayBlocked }
}
